import { useEffect, useMemo, useState } from "react";
import { authFetch } from "../../lib/auth";
import TipTapEditor from "../../components/admin/TipTapEditor";

export type PolicyKind = "terms" | "privacy";

type Props = {
  kind: PolicyKind;
  /** 관리자 레이아웃에서 이미 제목/설명을 렌더링하면 true */
  hideHeader?: boolean;
};

function formatKST(iso?: string | null) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleString("ko-KR", { timeZone: "Asia/Seoul" });
  } catch {
    return iso;
  }
}

export default function PolicyEditor({ kind, hideHeader = false }: Props) {
  const title = useMemo(
    () => (kind === "terms" ? "이용약관 관리" : "개인정보처리방침 관리"),
    [kind]
  );

  const readUrl = `/api/admin/legal/${kind}`;
  const saveUrl = `/api/admin/legal/${kind}`;

  const [loading, setLoading] = useState(false);
  const [html, setHtml] = useState("");
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const r = await authFetch(readUrl);
      const data = await r.json();

      if (!data?.ok) throw new Error(data?.message || "로드 실패");

      // ✅ admin_legal.js 응답: { ok:true, row:{ kind, html, updated_at } }
      const row = data?.row;
      setHtml(String(row?.html || ""));
      setUpdatedAt(row?.updated_at || null);
    } catch (e: any) {
      setError(e?.message || "로드 실패");
    } finally {
      setLoading(false);
    }
  }

  async function save() {
    setLoading(true);
    setError(null);
    try {
      const r = await authFetch(saveUrl, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ html }),
      });
      const data = await r.json();

      if (!data?.ok) throw new Error(data?.message || "저장 실패");

      // ✅ 저장 응답에도 row 반환하도록 해둔 상태: { ok:true, row:{...} }
      const row = data?.row;
      if (row?.updated_at) setUpdatedAt(row.updated_at);

      alert("저장되었습니다.");
    } catch (e: any) {
      setError(e?.message || "저장 실패");
      alert(e?.message || "저장 실패");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);

  return (
    <div className="jp-admin-settings space-y-4">
      {/* ✅ 중복되는 상단 블록 제거: hideHeader=true면 렌더링 안 함 */}
      {!hideHeader && (
        <div className="jp-admin-settings-heading">
          <h1 className="text-xl font-bold text-slate-900">{title}</h1>
          <p className="mt-1 text-sm text-slate-500">
            저장하면 사용자 화면에 즉시 반영됩니다.
          </p>
        </div>
      )}

      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {updatedAt && (
            <p className="text-xs text-slate-400">
              최종 저장: {formatKST(updatedAt)}
            </p>
          )}
          {error && (
            <p className="mt-1 text-sm text-rose-600">
              {error}
            </p>
          )}
        </div>

        <div className="flex shrink-0 gap-2">
          <button
            onClick={load}
            className="h-10 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
            disabled={loading}
          >
            새로고침
          </button>
          <button
            onClick={save}
            className="h-10 rounded-lg bg-slate-900 px-4 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-60"
            disabled={loading}
          >
            저장
          </button>
        </div>
      </div>

      <p className="text-sm text-slate-500">
        서식 도구를 눌러 제목, 목록, 링크 등을 쉽게 편집할 수 있습니다.
      </p>

      <TipTapEditor value={html} onChange={setHtml} />

      <div className="flex justify-end">
        <button
          onClick={save}
          className="h-10 rounded-lg bg-slate-900 px-6 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-60"
          disabled={loading}
        >
          저장
        </button>
      </div>
    </div>
  );
}
